import { useErrorHandlingQuery } from '@backoffice-frontend/data-fetching'
import { LoadingIndicator, Text } from '@moia-dev/pace-web'
import { useParams } from 'react-router'
import { useSharedMapContext } from '../SharedMapContext'
import { SAVED_ROUTE_DETAILS_FETCH_POLICY } from '../utils'
import { useRouteBuilderSavedRouteDetailsQuery } from './TripEditor.hook'
import type { EditingTrip } from './TripEditorView'
import { TripEditorView } from './TripEditorView'

type EditTripViewProps = {
  metropolisId: number
  metropolisName?: string
  mapCenter: [number, number]
}

export const EditTripView = ({
  metropolisId,
  metropolisName,
  mapCenter,
}: EditTripViewProps) => {
  const { id } = useParams<{ id: string }>()
  const { mapData } = useSharedMapContext()

  const { data, loading } = useErrorHandlingQuery(
    useRouteBuilderSavedRouteDetailsQuery,
    {
      variables: { id: id! },
      skip: !id,
      fetchPolicy: SAVED_ROUTE_DETAILS_FETCH_POLICY,
    },
  )

  const savedRouteDetails = data?.savedRouteDetails
  const savedTrip = savedRouteDetails?.trip

  if (loading || !savedRouteDetails || !savedTrip) {
    return (
      <div
        css={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          gap: 12,
        }}
      >
        {loading ? (
          <LoadingIndicator size={24} />
        ) : (
          <Text color="reduced">Trip not found</Text>
        )}
      </div>
    )
  }

  const trip = savedRouteDetails
  const segments = savedTrip.segments.flatMap(({ segment }) => {
    const desiredLocation =
      segment?.stop?.desiredLocation ?? segment?.destination

    if (!segment || !desiredLocation) return []

    return [{ segment, desiredLocation }]
  })
  const editingTrip: EditingTrip = {
    id: trip.id,
    tripId: savedTrip.id,
    name: trip.name,
    description: trip.description,
    tripType: savedTrip.type,
    segments: segments.map(({ segment: seg, desiredLocation }) => {
      return {
        id: seg.id,
        sequenceId: seg.sequenceId,
        metadata: seg.metadata,
        destination: {
          desiredLocation: {
            ...desiredLocation,
            heading: seg.stop?.desiredLocation?.heading || null,
          },
          snappedLocation: seg.stop?.snappedLocation,
          address: seg.stop?.desiredAddress,
          stopType: seg.stop?.destinationBehavior?.stopType,
          oddElementType: seg.stop?.oddElementType,
        },
        numPassengers: seg.numPassengers,
        route: seg.route,
      }
    }),
    codebeamerId: trip.assignedVtrs[0]?.codebeamerIds[0],
    associatedVtr: trip.assignedVtrs[0]?.vtr,
    assignedVtrs: trip.assignedVtrs,
    allWaypointsMode: savedTrip.allWaypointsMode,
  }

  return (
    <TripEditorView
      key={trip.id}
      metropolisId={metropolisId}
      metropolisName={metropolisName}
      mapCenter={mapCenter}
      editingTrip={editingTrip}
      initialOddElements={mapData.oddElements}
    />
  )
}
